import React, { useEffect, useState } from 'react';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { type FieldValues } from 'react-hook-form';

import {
    useFormaliteService,
    useSociete,
    useTiersService,
    useUtilisateurService,
    type UtilisateurTaches,
} from '@europrocurement/l2d-domain';
import {
    type TachesEtRappelsDesDossierApiApiTachesDossierGetCollectionRequest,
    type ApiTacheDossierPostRequestTache,
    type TachesEtRappelsDesDossierApiApiTacheDossierIdPatchRequest,
    type TachesEtRappelsDesDossierApiApiTacheDossierPostRequest,
    type TachesEtRappelsDesDossierTacheUpdate,
    type TblTacheTacheUpdatePriorityEnum,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material';
import {
    type TachesEtRappelsApiGetCollectionTachesEtRappelsCollectionRequest,
    type PostTachesEtRappelsCollectionRequestTache,
    type TachesEtRappelsApiPatchTachesEtRappelsItemRequest,
    type TachesEtRappelsApiPostTachesEtRappelsCollectionRequest,
    type TachesEtRappelsTacheUpdate,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import {
    type TachesEtRappelsDesUtilisateursJsonldPriorityEnum,
    type TachesEtRappelsDesUtilisateursApiApiTachePostRequest,
    type TachesEtRappelsDesUtilisateursApiApiTacheIdPatchRequest,
    type TachesEtRappelsDesUtilisateursPriorityEnum,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '@europrocurement/flexy-components/redux/storeConfig/store';
import { useUtilisateur } from '../../Utilisateur/hooks/useUtilisateur';
import TaskFormModal from '../components/TaskFormModal/TaskFormModal';

const TASK_DELETE_MESSAGES = {
    question: 'Supprimer cette tâche ?',
    detail: {
        message: 'Cette action est irréversible. La tâche sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

type UseTasksHookProps = {
    autoload?: boolean;
    apiTarget?: 'formalite' | 'tiers' | 'utilisateur';
    linkedEntityId?: string;
};

type DateFilters = {
    startDate?: string;
    endDate?: string;
};

type OnSuccessFormTaskProps = {
    taskForm: FieldValues;
    originalTask?: DossierTaches | PrescripteurTaches | UtilisateurTaches;
};

export type CallApiGetTachesParams = {
    linkedEntityId?: number;
    userIds?: number[];
    tacheExpiredAt?: string;
    tacheCreatedAt?: string;
    tacheIsDone?: boolean;
};

export type FormalistItem = {
    id: number;
    firstName: string;
    lastName: string;
    emailAddress: string;
    fullName: string;
    userIdentifier: string;
};

/**
 * Gestion des tâches et actions liées aux tâches sur des apis multiples
 * Ajouter l'entité concernée pour l'ajout d'une nouvelle tache liée dans le paramètre apiTarget
 *  -> Puis venir définir la route et les paramètre d'appels dans les méthodes callApi[...]
 */
const useTasksHook = ({
    autoload = true,
    apiTarget = 'formalite',
    linkedEntityId,
}: UseTasksHookProps) => {
    const { modalActions } = useModal();
    const { getDossierTaches, tachesEtRappelsDossierApi, formalisteApi } = useFormaliteService();
    const { getPrescripteursTaches, tachesEtRappelsTiersApi } = useTiersService();
    const { getUtilisateurTaches, tacheUtilisateurApi } = useUtilisateurService();
    const xIdSociete = useSociete();
    const dispatch = useDispatch<AppDispatch>();
    const { getCurrentUser } = useUtilisateur();
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();

    const [tasks, setTasks] = useState<
        DossierTaches[] | PrescripteurTaches[] | UtilisateurTaches[]
    >([]);
    const [orderedTasks, setOrderedTasks] = useState<
        DossierTaches[] | PrescripteurTaches[] | UtilisateurTaches[]
    >([]);
    const [tasksLoading, setTasksLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const refreshReduxStore = () => {
        if (apiTarget === 'formalite') {
            dispatch(getDossierTaches({}));
        } else if (apiTarget === 'tiers') {
            dispatch(getPrescripteursTaches({}));
        } else if (apiTarget === 'utilisateur') {
            dispatch(getUtilisateurTaches({}));
        }
    };
    const checkAndHydrateTaskValues = (addTaskForm: FieldValues) => {
        let hasError = false;
        let tache = null;

        let assignTo = null;
        let priority = 0;

        // Check send params
        if (
            addTaskForm.assignTo &&
            addTaskForm.assignTo.length === 1 &&
            addTaskForm.assignTo[0].id
        ) {
            assignTo = `${apiTarget}/user/${addTaskForm.assignTo[0].id}`;
        }
        if (addTaskForm.priority && addTaskForm.priority.value) {
            priority = addTaskForm.priority.value;
        }

        if (!assignTo) {
            enqueueSnackbar('Utilisateur non trouvé', {
                variant: 'error',
            });
            hasError = true;
        }
        tache = {
            assignTo: assignTo as string,
            description: addTaskForm.description || null,
            expiredAt: addTaskForm.expiredAt,
            priority,
            remindAt: addTaskForm.remindAt,
            title: addTaskForm.title,
        };
        return { hasError, tache };
    };

    // Switch api calls
    // Add Task
    const callApiGetTaches = async ({
        userIds,
        tacheExpiredAt,
        tacheCreatedAt,
        tacheIsDone,
    }: CallApiGetTachesParams) => {
        let tacheExpiredAtAfter: string | undefined;
        let tacheExpiredAtBefore: string | undefined;
        let tacheCreatedAtAfter: string | undefined;
        let tacheCreatedAtBefore: string | undefined;
        let iriUsers: string[] | undefined;

        if (tacheExpiredAt) {
            const filtersDate = tacheExpiredAt as DateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                tacheExpiredAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                tacheExpiredAtBefore = endedAt.toISOString();
            }
        }

        // Range of date to filter
        if (tacheCreatedAt) {
            const filtersDate = tacheCreatedAt as DateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                tacheCreatedAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                tacheCreatedAtBefore = endedAt.toISOString();
            }
        }

        if (userIds && userIds.length > 0) {
            iriUsers = userIds.map((userId) => `${apiTarget}/user/${userId}`);
        }

        const filterRequest:
            | TachesEtRappelsDesDossierApiApiTachesDossierGetCollectionRequest
            | TachesEtRappelsApiGetCollectionTachesEtRappelsCollectionRequest = {
            tacheExpiredAtAfter,
            tacheExpiredAtBefore,
            tacheCreatedAtAfter,
            tacheCreatedAtBefore,
            tacheAssignTo2: iriUsers,
            tacheIsDone,
        };

        if (apiTarget === 'formalite') {
            return tachesEtRappelsDossierApi.apiTachesDossierGetCollection({
                ...(linkedEntityId ? { dossier: linkedEntityId } : {}),
                ...filterRequest,
            });
        }
        if (apiTarget === 'tiers') {
            return tachesEtRappelsTiersApi.getCollectionTachesEtRappelsCollection({
                ...(linkedEntityId ? { tiers: linkedEntityId } : {}),
                xIdSociete,
                ...filterRequest,
            });
        }

        return tacheUtilisateurApi.apiTachesGetCollection({
            assignTo2: filterRequest.tacheAssignTo2,
            expiredAtAfter: filterRequest.tacheExpiredAtAfter,
            expiredAtBefore: filterRequest.tacheExpiredAtBefore,
            createdAtAfter: filterRequest.tacheCreatedAtAfter,
            createdAtBefore: filterRequest.tacheCreatedAtBefore,
            isDone: filterRequest.tacheIsDone,
        });
    };

    // Add Task
    const callApiAddTache = async (
        tache: ApiTacheDossierPostRequestTache | PostTachesEtRappelsCollectionRequestTache,
    ) => {
        if (apiTarget === 'formalite' && linkedEntityId) {
            const request: TachesEtRappelsDesDossierApiApiTacheDossierPostRequest = {
                apiTacheDossierPostRequest: {
                    dossier: parseInt(linkedEntityId, 10),
                    tache,
                },
            };
            return tachesEtRappelsDossierApi.apiTacheDossierPost(request);
        }
        if (apiTarget === 'tiers' && linkedEntityId) {
            const request: TachesEtRappelsApiPostTachesEtRappelsCollectionRequest = {
                postTachesEtRappelsCollectionRequest: {
                    tache,
                    tiers: linkedEntityId,
                },
                xIdSociete,
            };
            return tachesEtRappelsTiersApi.postTachesEtRappelsCollection(request);
        }

        const request: TachesEtRappelsDesUtilisateursApiApiTachePostRequest = {
            tachesEtRappelsDesUtilisateursJsonld: {
                ...tache,
                priority: tache.priority as TachesEtRappelsDesUtilisateursJsonldPriorityEnum,
            },
        };
        return tacheUtilisateurApi.apiTachePost(request);
    };
    // Edit Task
    const callApiEditTache = async (
        tache:
            | TachesEtRappelsDesDossierTacheUpdate
            | TachesEtRappelsTacheUpdate
            | UtilisateurTaches,
        originalTacheId: string,
    ) => {
        if (apiTarget === 'formalite') {
            const request: TachesEtRappelsDesDossierApiApiTacheDossierIdPatchRequest = {
                id: originalTacheId,
                tachesEtRappelsDesDossierTacheUpdate: tache as TachesEtRappelsDesDossierTacheUpdate,
            };
            return tachesEtRappelsDossierApi.apiTacheDossierIdPatch(request);
        }
        if (apiTarget === 'tiers') {
            const request: TachesEtRappelsApiPatchTachesEtRappelsItemRequest = {
                id: originalTacheId,
                tachesEtRappelsTacheUpdate: tache as TachesEtRappelsTacheUpdate,
                xIdSociete,
            };

            return tachesEtRappelsTiersApi.patchTachesEtRappelsItem(request);
        }

        const request: TachesEtRappelsDesUtilisateursApiApiTacheIdPatchRequest = {
            id: originalTacheId,
            tachesEtRappelsDesUtilisateurs: {
                assignTo: tache.tache?.assignTo as string,
                description: tache.tache?.description,
                title: tache.tache?.title,
                remindAt: tache.tache?.remindAt,
                expiredAt: tache.tache?.expiredAt,
                priority: tache.tache?.priority as TachesEtRappelsDesUtilisateursPriorityEnum,
            },
        };

        return tacheUtilisateurApi.apiTacheIdPatch(request);
    };
    // Check task
    const callApiCheckTache = async (linkedTaskId: string, currentCheckValue: boolean) => {
        if (apiTarget === 'formalite') {
            return tachesEtRappelsDossierApi.apiTacheDossierIdPatch({
                id: linkedTaskId,
                tachesEtRappelsDesDossierTacheUpdate: {
                    tache: {
                        isDone: !currentCheckValue,
                    },
                },
            });
        }
        if (apiTarget === 'tiers') {
            return tachesEtRappelsTiersApi.patchTachesEtRappelsItem({
                id: linkedTaskId,
                xIdSociete,
                tachesEtRappelsTacheUpdate: {
                    tache: {
                        isDone: !currentCheckValue,
                    },
                },
            });
        }

        return tacheUtilisateurApi.apiTacheIdPatch({
            id: linkedTaskId,
            tachesEtRappelsDesUtilisateurs: {
                isDone: !currentCheckValue,
            },
        });
    };

    // Delete Task
    const callApiDeleteTache = async (id: string) => {
        if (apiTarget === 'formalite') {
            return tachesEtRappelsDossierApi.apiTacheDossierIdDelete({
                id: id.toString(),
            });
        }
        if (apiTarget === 'tiers') {
            return tachesEtRappelsTiersApi.deleteTachesEtRappelsItem({
                id,
                xIdSociete,
            });
        }
        return tacheUtilisateurApi.apiTacheIdDelete({
            id,
        });
    };

    // search formalist
    const onSearchUsers = (term: string): Promise<Array<FormalistItem>> =>
        new Promise((resolve, reject) => {
            let searchTimeout: number | undefined;

            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
                formalisteApi
                    .apiFormalistesGetCollection({
                        fullname: term,
                    })
                    .then((res) => {
                        const items = res.data['hydra:member'] || [];

                        resolve(
                            items
                                .map((item) => ({
                                    id: item?.id as number,
                                    firstName: item.nom as string,
                                    lastName: item.prenom as string,
                                    fullName: `${item.prenom} ${item.nom}` as string,
                                    emailAddress: item.mail as string,
                                    userIdentifier: item.userIdentifier as string,
                                }))
                                .filter(
                                    (item) => item.firstName && item.lastName && item.emailAddress,
                                ),
                        );
                    })
                    .catch((err) => reject(err));
            }, 300);
        });

    // Add task method and modal

    const onAddTask = async ({ taskForm }: OnSuccessFormTaskProps) => {
        const { hasError, tache } = checkAndHydrateTaskValues(taskForm);
        if (hasError || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        if (apiTarget !== 'utilisateur' && !linkedEntityId) {
            enqueueSnackbar(
                `${apiTarget === 'formalite' ? 'Dossier' : 'Prescripteur'}  non trouvé`,
                {
                    variant: 'error',
                },
            );
        } else {
            const response = await callApiAddTache(tache);

            if ([200, 201].includes(response.status)) {
                enqueueSnackbar('Tâche ajoutée');
                // Update context state
                const createdTask = response.data;
                setTasks([...tasks, createdTask] as
                    | DossierTaches[]
                    | PrescripteurTaches[]
                    | UtilisateurTaches[]);

                refreshReduxStore();
                setIsSubmitting(false);
                modalActions.close();
            } else {
                enqueueSnackbar("Une erreur est survenue lors de l'ajout de la tâche", {
                    variant: 'error',
                });
                setIsSubmitting(false);
            }
        }
    };

    const handleAddTask = async () => {
        if (apiTarget !== 'utilisateur' && !linkedEntityId) {
            enqueueSnackbar(
                `${apiTarget === 'formalite' ? 'Dossier' : 'Prescripteur'}  non trouvé`,
                {
                    variant: 'error',
                },
            );
        }

        try {
            const user = await getCurrentUser();
            modalActions.call(
                <TaskFormModal
                    onSuccess={onAddTask}
                    onSearchUsers={onSearchUsers}
                    currentUser={user}
                />,
            );
        } catch (err) {
            console.log(err);
            enqueueSnackbar("Une erreur est survenue lors de la récupération de l'utilisateur", {
                variant: 'error',
            });
        }
    };

    const handleCheckTask = async (
        originalTask: DossierTaches | PrescripteurTaches | UtilisateurTaches,
    ) => {
        if (originalTask.id && originalTask.tache) {
            const currentCheckValue = originalTask.tache.isDone || false;
            const updateResponse = await callApiCheckTache(
                originalTask.id.toString(),
                currentCheckValue,
            );
            if (updateResponse.status === 200) {
                setTasks(
                    tasks.map((task) => {
                        if (task.id === originalTask.id) {
                            return {
                                ...task,
                                tache: { ...task.tache, isDone: !currentCheckValue },
                            };
                        }
                        return task;
                    }) as DossierTaches[] | PrescripteurTaches[] | UtilisateurTaches[],
                );
                refreshReduxStore();
            } else {
                enqueueSnackbar('Une erreur est survenue lors de la modification de la tâche');
            }
        }
    };

    // Edit task method and modal
    const onEditTask = async ({ taskForm, originalTask }: OnSuccessFormTaskProps) => {
        const { hasError, tache } = checkAndHydrateTaskValues(taskForm);

        const originalTache = originalTask?.tache;

        if (
            hasError ||
            isSubmitting ||
            !originalTache ||
            !originalTache.id ||
            !originalTask ||
            !originalTask.id
        ) {
            enqueueSnackbar("Une erreur est survenue lors de l'édition de la tâche", {
                variant: 'error',
            });
            return;
        }

        const updatedTache = {
            tache: {
                ...originalTache,
                assignTo:
                    tache.assignTo !== originalTache.assignTo
                        ? (tache.assignTo as string)
                        : (originalTache.assignTo as string),
                title: tache.title !== originalTache.title ? tache.title : originalTache.title,
                description:
                    tache.description !== originalTache.description
                        ? tache.description
                        : originalTache.description,
                priority:
                    tache.priority !== originalTache.priority
                        ? (tache.priority as TblTacheTacheUpdatePriorityEnum)
                        : (originalTache.priority as TblTacheTacheUpdatePriorityEnum),
                remindAt:
                    tache.remindAt !== originalTache.remindAt
                        ? tache.remindAt
                        : originalTache.remindAt,
                expiredAt:
                    tache.expiredAt !== originalTache.expiredAt
                        ? tache.expiredAt
                        : originalTache.expiredAt,
            },
        };

        setIsSubmitting(true);

        try {
            const response = await callApiEditTache(updatedTache, originalTask.id.toString());
            if ([200, 201].includes(response.status)) {
                enqueueSnackbar('Tâche modifiée');

                // Update context state
                setTasks(
                    (tasksState) =>
                        tasksState.map((task) => {
                            if (task.id?.toString() === response.data.id?.toString()) {
                                if (apiTarget === 'utilisateur') {
                                    return { ...task, tache: response.data };
                                }

                                return response.data;
                            }
                            return task;
                        }) as DossierTaches[] | PrescripteurTaches[] | UtilisateurTaches[],
                );
                refreshReduxStore();
                setIsSubmitting(false);
                modalActions.close();
            } else {
                enqueueSnackbar("Une erreur est survenue lors de l'ajout de la tâche", {
                    variant: 'error',
                });
                setIsSubmitting(false);
            }
        } catch (err) {
            console.log(err);
            enqueueSnackbar("Une erreur est survenue lors de l'ajout de la tâche", {
                variant: 'error',
            });
            setIsSubmitting(false);
        }
    };

    const handleEditTask = async (
        originalTask: DossierTaches | PrescripteurTaches | UtilisateurTaches,
    ) => {
        if (!originalTask) {
            enqueueSnackbar('Tâche non trouvée', {
                variant: 'error',
            });
            return;
        }
        const user = await getCurrentUser();

        modalActions.call(
            <TaskFormModal
                onSuccess={onEditTask}
                onSearchUsers={onSearchUsers}
                originalTask={originalTask}
                currentUser={user}
            />,
        );
    };

    // Delete task method and modal
    const onDeleteTask = async (id: number) => {
        if (!id || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        const response = await callApiDeleteTache(id.toString());

        if ([204].includes(response.status)) {
            enqueueSnackbar('Tâche supprimée');
            // Update context state
            setTasks(
                (tasksState) =>
                    tasksState.filter((task) => task.id !== id) as
                        | DossierTaches[]
                        | PrescripteurTaches[]
                        | UtilisateurTaches[],
            );
            refreshReduxStore();
            setIsSubmitting(false);
            modalActions.close();
        } else {
            enqueueSnackbar("Une erreur est survenue lors de l'ajout de la tâche", {
                variant: 'error',
            });
            setIsSubmitting(false);
        }
    };

    const handleDeleteTask = (dossierTaskId: number) => {
        modalActions.call(
            <ConfirmationModal
                messages={TASK_DELETE_MESSAGES}
                color={palette.error}
                disabled={isSubmitting}
                actionOnValidation={() => {
                    onDeleteTask(dossierTaskId);
                }}
                actionOnCancellation={() => modalActions.close()}
            />,
        );
    };

    const loadTasks = async ({
        userIds,
        tacheExpiredAt,
        tacheCreatedAt,
        tacheIsDone,
    }: CallApiGetTachesParams) => {
        const tasksResponse = await callApiGetTaches({
            userIds,
            tacheExpiredAt,
            tacheCreatedAt,
            tacheIsDone,
        });

        // Update structure of unlinked tasks (utilisateur wich is directly a call to TblTask) to homogenize tasks
        if (apiTarget === 'utilisateur') {
            setTasks(
                tasksResponse.data['hydra:member'].map((task) => ({
                    tache: task,
                    id: task.id?.toString(),
                })) as UtilisateurTaches[],
            );
        } else {
            setTasks(
                (tasksResponse.data['hydra:member'] as DossierTaches[] | PrescripteurTaches[]) ||
                    [],
            );
        }
    };

    useEffect(() => {
        const runLoadTask = async () => {
            setTasksLoading(true);
            if (linkedEntityId || apiTarget === 'utilisateur') {
                await loadTasks({});
            }

            setTasksLoading(false);
        };

        if (!tasksLoading && autoload) {
            runLoadTask();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedEntityId]);

    // Reorder tasks
    // On tasks change, sort tasks by status and expiration dates
    useEffect(() => {
        setOrderedTasks(
            tasks.sort((a, b) => {
                const aDone = a.tache?.isDone ? 1 : 0;
                const bDone = b.tache?.isDone ? 1 : 0;

                const aExpiredAt = a.tache?.expiredAt
                    ? new Date(a.tache.expiredAt).getTime()
                    : Infinity;

                const bExpiredAt = b.tache?.expiredAt
                    ? new Date(b.tache.expiredAt).getTime()
                    : Infinity;

                return aDone - bDone || aExpiredAt - bExpiredAt;
            }) as DossierTaches[] | PrescripteurTaches[] | UtilisateurTaches[],
        );
    }, [tasks]);

    return {
        handleAddTask,
        handleCheckTask,
        handleEditTask,
        handleDeleteTask,
        orderedTasks,
        loadTasks,
        setTasks,
        tasksLoading,
        onSearchUsers,
    };
};

export default useTasksHook;
